import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { PIPEFITTER_FETCH_PROJECT_SUMMARY_DATA } from '../../../redux/Pipefitter/types';
import { PipeCompatibility } from './PipeCompatibility';
import type { DashboardProject } from '../../project/types/project';

interface PipeCompatibilityReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: DashboardProject;
  selectedPipe?: { pipeId: string; displayName?: string; pipeName?: string } | null;
}

export const PipeCompatibilityReportModal: React.FC<PipeCompatibilityReportModalProps> = ({
  open,
  onOpenChange,
  project,
  selectedPipe,
}) => {
  const dispatch = useDispatch();
  const projectNumber = project?.project?.projectNumber ?? '';
  const projectName = project?.project?.projectName ?? '';
  const pipeId = selectedPipe?.pipeId;

  const isReportsLoading = useSelector((state: any) => state.pipefitterReports?.isReportsLoading ?? false);
  const projectSummaryData = useSelector((state: any) => state.pipefitterReports?.projectSummaryData ?? {});

  const pipeSizeLabel =
    selectedPipe?.displayName ?? selectedPipe?.pipeName ?? selectedPipe?.pipeId ?? '';

  const headerInfoItems = useMemo(() => {
    const items: { label: string; value: string; bgColor: string }[] = [];
    if (projectName) {
      items.push({ label: 'Project Name', value: projectName, bgColor: 'bg-indigo-100 text-indigo-800' });
    }
    if (projectNumber) {
      items.push({ label: 'Project Number', value: projectNumber, bgColor: 'bg-cyan-100 text-cyan-800' });
    }
    if (pipeSizeLabel) {
      items.push({
        label: 'Pipe size',
        value: pipeSizeLabel,
        bgColor: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]',
      });
    }
    return items;
  }, [projectName, projectNumber, pipeSizeLabel]);

  useEffect(() => {
    if (!open || !projectNumber || !pipeId) return;
    dispatch({
      type: PIPEFITTER_FETCH_PROJECT_SUMMARY_DATA,
      payload: { projectNumber, pipeId },
    });
  }, [open, projectNumber, pipeId, dispatch]);

  const missingContext = !projectNumber || !pipeId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-screen max-w-none max-h-[100dvh] m-0 p-0 gap-0 border-0 bg-page-bg shadow-none rounded-none [&>button]:hidden flex flex-col overflow-hidden">
        <DialogHeader className="px-4 sm:px-6 py-4 border-b border-[#E2E5EA] flex-shrink-0 bg-white">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              className="shrink-0 h-9 w-9 p-0 border-[#E2E5EA] hover:bg-page-bg"
              variant="outline"
              size="icon"
              aria-label="Back to pipe data report"
            >
              <ArrowLeft className="h-4 w-4 text-text-grey" />
            </Button>
            <div className="min-w-0">
              <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
                Pipe Compatibility Report
              </DialogTitle>
              {headerInfoItems.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {headerInfoItems.map((item, idx) => (
                    <span
                      key={`${item.label}-${idx}`}
                      className={`${item.bgColor} px-2 py-1 rounded text-xs font-medium`}
                    >
                      {item.label}: {item.value}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 px-4 sm:px-6 min-h-0">
          {missingContext ? (
            <div className="rounded-lg border border-[rgba(235,28,55,0.2)] bg-[rgba(235,28,55,0.06)] p-4 text-[13px] text-hero-red">
              Select a pipe size on the Pipefitter page to load compatibility data.
            </div>
          ) : (
            <PipeCompatibility
              projectSummaryData={projectSummaryData}
              projectNumber={projectNumber}
              isLoading={isReportsLoading}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
